import Guard from 'arg-guard'

/**
 * A piece of data related to a property which can be edited.
 *
 * @export
 * @class EditablePropertyDataItem
 */
export default class EditablePropertyDataItem {

    /**
     *Creates an instance of EditablePropertyDataItem.
     * @param {string} title The title of the editable field.
     * @param {string} dottedString The dotted string to uniquely identify the field.
     * @param {any} initialValue The initial value of the field.
     * @param {any} editedValue An optional edited value for the field.
     * @memberof EditablePropertyDataItem
     */
    constructor(title, dottedString, initialValue, editedValue) {
        Guard.throwIf(title, 'title')
        Guard.throwIf(dottedString, 'dottedString')
        Guard.throwIfUndefined(initialValue, 'initialValue')

        this.title = title
        this.dottedString = dottedString
        this.initialValue = initialValue
        this.editedValue = editedValue !== undefined ? editedValue : initialValue
        this.payload = null
    }

    /**
     * Indicates if the value has been changed.
     *
     * @readonly
     * @memberof EditablePropertyDataItem
     */
    get changed() {
        return this.initialValue !== this.editedValue
    }

    /**
     * Returns the object in a form suitable to store.
     *
     * @memberof EditablePropertyDataItem
     */
    toStoreJson() {
        const retVal = {
            title: this.title,
            dottedString: this.dottedString,
            value: {
                initial: this.initialValue,
                edited: this.editedValue,
            },
        }

        if (this.payload)
            retVal.payload = this.payload

        return retVal
    }

    /**
     * Ensures that the provided data is already of this type. If not, it treats the value as store JSON.
     *
     * @static
     * @param {Object} data Source data - From state.
     * @returns {EditablePropertyDataItem} Either the data is passed back if already of this type or the result of the fromStoreJson method is.
     * @memberof EditablePropertyDataItem
     */
    static ensure(data) {
        return data && data instanceof EditablePropertyDataItem ? data : EditablePropertyDataItem.fromStoreJson(data)
    }

    /**
     * Returns the object type from segment data.
     * @param {Object} [data=null] The segment data.
     * @memberof EditablePropertyDataItem
     */
    static fromSegmentData(data = null) {
        return data ? new EditablePropertyDataItem(data.title, data.dottedString, data.type == 'Selection' ? data.id : data.value) : null
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {Object} [data=null] The stored data.
     * @memberof EditablePropertyDataItem
     */
    static fromStoreJson(data = null) {
        const retVal = data ? new EditablePropertyDataItem(data.title, data.dottedString,
            data.value ? data.value.initial : null, data.value ? data.value.edited : null) : null

        if (data && data.payload)
            retVal.payload = data.payload

        return retVal
    }
}
